import { Component, Input, OnChanges, Output, EventEmitter, SimpleChanges, AfterViewInit, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { AuthService, BreadcrumbService, CompanyViewService, ProductService, ProductTypeWithColor, StateService, Utils } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BrandFilterOption, Company, CompanyFilterOption, EnvironmentName, GetListProductsFiltersRequest, GetListProductsFiltersResponse, GetListRequestedProductsFiltersRequest, GetListRequestedProductsFiltersResponse, ListRequestedProductsRequest, NewListProductsRequest, Product2, ProductClassificationFilterOption, ProductName, ProductSpecificationFilterGroup, ProductStateFilterOption, ProductTypeFilterOption, ProductWorkflow, ProductWorkflowState, RangeFilterOption, RequestedProduct, SituViewWebApiClient, WorkflowStateType, ListProductsRequestOrderField } from 'situview-webapi-client';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { CreateGenericArtifactRequest, CreateGenericArtifactResponse, HomeStatus, SituRenderServiceWebApiClient } from 'siturenderservice-webapi-client';
import { ConfigService } from 'src/app/services/config.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { Observable, Subject, fromEvent, interval, of} from 'rxjs';
import { concatMap, debounceTime, map, startWith, takeWhile } from 'rxjs/operators';
import { Role } from 'src/app/shared/models/role.model';
import { MatDialog } from '@angular/material/dialog';
import { ProductDownloadDialogComponent } from '../product-download-dialog/product-download-dialog.component';
import { ProductDownloadRequest } from '../thumb-product-card/thumb-product-card.component';
import { Guid } from 'guid-typescript';

@Component({
  selector: 'product-catalog-list',
  templateUrl: './product-catalog-list.component.html',
  styleUrls: ['./product-catalog-list.component.scss']
})

export class ProductCatalogListComponent extends SituBaseComponent implements OnChanges, AfterViewInit {
  @Input() companyIdFilter: string | undefined;
  @Input() brandIdFilter: string | undefined;
  @Input() productClassificationIdFilter: string | undefined;
  @Input() productTypeIdFilter: string | undefined;
  @Input() productStateFilter: ProductWorkflowState | undefined = ProductWorkflowState.Live;
  @Input() templateNameFilter: string | undefined;
  @Input() tagGroupsFilter: string[] | undefined;
  @Input() productSpecificationFilterGroups: ProductSpecificationFilterGroup[] | undefined;
  @Input() productSpecificationSelectedIds: string[] = [];
  @Input() productRangeIdFilter: string | undefined;
  //@Input() autoRangeFilter: boolean = false;
  @Input() includeRanges: boolean = false;
  @Input() relatedTagsOrder?: string[];
  @Input() searchTerm: string = "";
  @Input() compact: boolean = false;
  @Input() isCatalog: boolean = false;
  @Input() editProductOnClick: boolean = false;
  @Input() multiSelect: boolean = false;
  @Input() allowRequestedProducts: boolean = false;
  @Output() onProductSelected: EventEmitter<(Product2 | RequestedProduct)[]> = new EventEmitter();
  @Output() onFilterCompanyId: EventEmitter<string> = new EventEmitter();
  @Output() onFilterBrandId: EventEmitter<string> = new EventEmitter();
  @Output() onFilterProductClassificationId: EventEmitter<string> = new EventEmitter();
  @Output() onFilterProductTypeId: EventEmitter<string> = new EventEmitter();
  @Output() onFilterProductSpecificationGroups: EventEmitter<ProductSpecificationFilterGroup[]> = new EventEmitter();
  @Output() onFilterProductSpecificationSelectedIds: EventEmitter<string[]> = new EventEmitter();
  @Output() onFilterProductState: EventEmitter<ProductWorkflowState> = new EventEmitter();
  @Output() onFilterProductRangeId: EventEmitter<string> = new EventEmitter();
  @Output() onSearchTermChanged: EventEmitter<string> = new EventEmitter();

  /* Search */
  pageSize = this.compact ? 8 : 20;
  pageNumber = 1;
  totalCount = 0;

  fullCatalogAccess: boolean = false;
  
  productClassificationNameFilter: string | undefined;
  productTypeNameFilter: string | undefined;
  companyNameFilter: string | undefined;
  brandNameFilter: string | undefined;
  productStateNameFilter: string | undefined;
  productRangeNameFilter: string | undefined;

  loadingFilterOption = false;
  hasFilters = false;

  enabledProductsFilter = true;
  requestedProductsFilter = false;
  orderByDateModified = false;

  workflowStateType = WorkflowStateType;

  /* Data */
  products: Product2[] = [];
  selectedProducts: Product2[] = [];

  requestedProducts: RequestedProduct[] = [];
  selectedRequestedProducts: RequestedProduct[] = [];

  readonly SituViewUrl: string;

  /* Loading */
  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    private snackBar: MatSnackBar,
    private productService: ProductService,
    private situViewClient: SituViewWebApiClient,
    private companyViewService: CompanyViewService,
    private renderServiceClient: SituRenderServiceWebApiClient,
    private configService: ConfigService,
    private changeDetectorRef: ChangeDetectorRef,
    private clipboard: Clipboard,
    private dialog: MatDialog) {
    super(authService, stateService);
    this.SituViewUrl = configService.config.situViewWebApiBaseUrl;

    this._subscriptions.push(this.searchTermChangedSubject.pipe( debounceTime(600) )
    .subscribe(() => {
      this.searchTermChanged();
    }))
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.fullCatalogAccess = this.authService.hasRole(Role.ProductCatalog) || this.authService.hasSituAccess();

    this.filteredCompanies = this.companyControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterCompanies(value);
      })
    );

    this.filteredBrands = this.brandControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterBrands(value);
      })
    );

    this.filteredProductRanges = this.productRangeControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        if (typeof value === 'string') {
          return this.filterProductRanges(value);
        } else {
          return this.filterProductRanges();
        }
      })
    );

    this.filteredProductClassifications = this.productClassificationControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductClassifications(value);
      })
    );

    this.filteredProductTypes = this.productTypeControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductTypes(value);
      })
    );

    this.filteredProductStates = this.productStateControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductStates(value);
      })
    );
  }

  protected onSelectedCompanyChange(): void {
    this.loadCompanies();
    
    this.loadProductTypes();

    this.loadProducts(true);

    this.loadAllFilterOptions();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.observer && this.showMoreProductsMarker?.nativeElement) {
      this.observer.unobserve(this.showMoreProductsMarker.nativeElement);
    }
    if (this.searchTermChangedSubject) {
      this.searchTermChangedSubject.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.companyIdFilter &&
      (changes.companyIdFilter.currentValue !== changes.companyIdFilter.previousValue ||
      changes.companyIdFilter.currentValue !== this.companyIdFilter)) {
      if (changes.companyIdFilter.currentValue) {
        this.selectCompanyByProductCompanyId(changes.companyIdFilter.currentValue);
      } else {
        this.clearCompany();
      }
    }

    if (changes.brandIdFilter &&
      (changes.brandIdFilter.currentValue !== changes.brandIdFilter.previousValue ||
      changes.brandIdFilter.currentValue !== this.brandIdFilter)) {
      if (changes.brandIdFilter.currentValue) {
        this.selectBrandByProductBrandId(changes.brandIdFilter.currentValue);
      } else {
        this.clearBrand();
      }
    }

    if (changes.rangeIdFilter &&
      (changes.rangeIdFilter.currentValue !== changes.previousValue ||
      changes.rangeIdFilter.currentValue !== this.productRangeIdFilter)) {
      if (changes.rangeIdFilter.currentValue) {
        this.selectProductRangeById(changes.rangeIdFilter.currentValue);
      } else {
        this.clearProductRange();
      }
    }

    if (changes.productClassificationFilter &&
      (changes.productClassificationFilter.currentValue !== changes.productClassificationFilter.previousValue ||
      changes.productClassificationFilter.currentValue !== this.productClassificationIdFilter)) {
      if (changes.productClassificationFilter.currentValue) {
        this.selectProductClassificationById(changes.productClassificationFilter.currentValue);
      } else {
        this.clearProductClassification();
      }
    }

    if (changes.productTypeFilter &&
      (changes.productTypeFilter.currentValue !== changes.productTypeFilter.previousValue ||
      changes.productTypeFilter.currentValue !== this.productTypeIdFilter)) {
      if (changes.productTypeFilter.currentValue) {
        this.selectProductTypeById(changes.productTypeFilter.currentValue);
      } else {
        this.clearProductType();
      }
    }

    if (changes.productStateFilter &&
      (changes.productStateFilter.currentValue !== changes.productStateFilter.previousValue ||
      changes.productStateFilter.currentValue !== this.productStateFilter)) {
      if (changes.productStateFilter.currentValue) {
        this.selectProductState(changes.productStateFilter.currentValue);
      } else {
        this.clearProductState();
      }
    } else if (changes.productStateFilter && changes.productStateFilter.firstChange && !changes.productStateFilter.currentValue) {
      this.selectProductState(ProductWorkflowState.Live);
    }

    if (changes.searchTerm && !changes.searchTerm.firstChange &&
      (changes.searchTerm.currentValue !== changes.searchTerm.previousValue ||
      changes.searchTerm.currentValue !== this.searchTerm)) {
      this.searchTerm = changes.searchTerm.currentValue;
      this.searchTermChanged();
    }

    if (changes.compact && changes.compact.currentValue !== changes.compact.previousValue) {
      this.pageSize = this.compact ? 8 : 20;
    }
  }

  @ViewChild('showMoreProductsMarker') showMoreProductsMarker: ElementRef | undefined;
  private observer: IntersectionObserver | undefined;
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.hasMoreProductsToShow()) {
          this.showMoreProducts();
        }
      });
    });

    if (this.showMoreProductsMarker?.nativeElement) {
      this.observer.observe(this.showMoreProductsMarker.nativeElement);
    }
  }

  async showMoreProducts() {
    this.pageNumber++;
    await this.loadProducts(false);
  }

  async loadAllProducts() {
    while(this.hasMoreProductsToShow()) {
      await this.showMoreProducts();
    }
  }

  hasMoreProductsToShow(): boolean {
    if (this.products?.length > 0) {
      return this.products.length < this.totalCount;
    }

    if (this.requestedProducts?.length > 0) {
      return this.requestedProducts.length < this.totalCount;
    }

    return false;
  }

  share(artifactId: string) {
    let link = this.configService.config.situLinkBaseUrl ?
          `https://product.${this.configService.config.situLinkBaseUrl}/${artifactId}` :
          `${this.configService.config.situPortalBaseUrl}/${artifactId}`;
    this.clipboard.copy(link);
    this.showMessage("Copied situLINK to clipboard!");
  }

  getProductTypeColor(productTypeId: string) {
    return this.allProductTypes.find(x => x.id === productTypeId)?.color;
  }

  getProductTypeName(productTypeId: string) {
    return this.allProductTypes.find(x => x.id === productTypeId)?.name;
  }

  getCompanyLogo(companyId: string) {
    return this.allCompanies.find(x => x.id === companyId)?.logoUrl;
  }

  getCompanyName(companyId: string) {
    return this.allCompanies.find(x => x.id === companyId)?.name;
  }

  getPreview(assetKey: string) {
    let request = new CreateGenericArtifactRequest();
    let companyPart = assetKey?.split("\\")[1];
    if (companyPart) {
      let company = this.allCompanies.find(x => x.name == companyPart);
      request.companyId = company ? company.id : this.selectedCompany?.id;
    } else {
      request.companyId = this.selectedCompany?.id;
    }
    request.callerReferenceKey = assetKey?.replace("assetinstance\\", "assetpreview\\");
    request.environment = EnvironmentName.Latest;
    request.environmentToUse = ProductName.Panorama;

    this.renderServiceClient.createGenericArtifact(request)
      .then((response: CreateGenericArtifactResponse) => {
        if (response.success) {
          if (response.status === HomeStatus.Complete) {
            this.showMessage("The Product Preview is complete!");
          } else {
            this.showMessage("We are generating a Product Preview!");
          }
        } else {
          this.showMessage("We could not generate the Product Preview!");
        }
      });
  }

  onProductDownloadRequest(request: ProductDownloadRequest) {
    this.dialog.open(ProductDownloadDialogComponent, {
      data: {
        url: `${this.configService.config.situViewWebApiBaseUrl}/home/getglb?artifactId=${request.artifactId}&filename=${request.file}`,
        fileName: request.fileName
      }
    });
  }

  onSelectProduct(product: Product2 | RequestedProduct) {
    if (!this.multiSelect) {
      this.selectedProducts = [product];

      this.onProductSelected.emit(this.selectedProducts);
    } else {
        if (this.isSelectedProduct(product)) {
          this.selectedProducts = this.selectedProducts.filter(x => x.id !== product.id);
        } else {
          this.selectedProducts.push(product);
        }
    }
  }

  isSelectedProduct(product: Product2 | RequestedProduct): boolean {
    // Don't show selection in catalog mode
    return !this.isCatalog && this.selectedProducts.some(x => x.id === product.id);
  }

  chooseSelectProducts() {
    var products = this.getSelectedProducts();
    this.onProductSelected.emit(products);
  }

  hasSelectedAllProducts(): boolean {
    return this.products.every(x => this.isSelectedProduct(x));
  }

  selectAllProducts(select: boolean) {
    if (select) {
      this.products.forEach(x => {
        if (!this.isSelectedProduct(x)) {
            this.selectedProducts.push(x);
          }
      });
    } else {
      this.selectedProducts = [];
    }
  }

  getSelectedProducts(): (Product2 | RequestedProduct)[] {
    return this.selectedProducts;
  }

  getSelectedProductCount(): number {
    return this.getSelectedProducts().length;
  }

  private searchTermChangedSubject = new Subject();
  debounceSearchTermChanged() {
    this.searchTermChangedSubject.next();
  }

  searchTermChanged() {
    this.onSearchTermChanged.emit(this.searchTerm);

    this.filterProducts();

    this.clearAllFilterOptions();
  }

  clearSearchTerm() {
    this.searchTerm = '';

    this.searchTermChanged();
  }

  onEnabledProductsFilter() {
    this.filterProducts();
  }

  async onRequestedProductsFilter() {
    if (this.productStateFilter != undefined) {
      this.productStateFilter = this.requestedProductsFilter ? ProductWorkflowState.NewProduct : ProductWorkflowState.Live;
    }

    this.clearAllFilterOptions();
    this.onClearProductRange();

    this.filterProducts();
    this.loadFilterProductStateOptions();
  }

  async filterProducts() {
    this.pageNumber = 1;
    await this.loadProducts(true);
  }

  // When a filter change arrives while a load is already in flight (e.g. spec
  // filter groups settling after the initial fetch starts), queue one re-run
  // so the filter isn't silently dropped. Multiple concurrent calls collapse
  // onto a single pending re-run.
  private loadProductsPending = false;
  private loadProductsPendingClearExisting = true;

  async loadProducts(clearExistingProducts: boolean) {
    if (!this.selectedCompany?.id) {
      return;
    }
    if (this.loading) {
      this.loadProductsPending = true;
      this.loadProductsPendingClearExisting = clearExistingProducts;
      return;
    }

    this.loading = true;

    if (!this.requestedProductsFilter) {
      // Get products
      try {
        var listProductsRequest = this.buildListProductsRequest();

        var productPage = await this.situViewClient.newListProducts(listProductsRequest);

        var products = productPage.items!;
        this.requestedProducts = [];
        if (clearExistingProducts) {
          this.products = products;
        } else {
          this.products.push(...products);
        }

        this.totalCount = productPage.totalItems!;

      } catch {
        this.showMessage("There was an issue loading products. Please refresh the page to try again.");
      } finally {
        this.loading = false;
        this.runPendingLoadProducts();
      }
    } else {
      // Get Requested products
      try {
        var listRequestedProductsRequest = this.buildListRequestedProductsRequest();

        var requestedProductPage = await this.situViewClient.listRequestedProducts(listRequestedProductsRequest);

        var requestedProducts = requestedProductPage.items!;
        this.products = [];
        if (clearExistingProducts) {
          this.requestedProducts = requestedProducts;
        } else {
          this.requestedProducts.push(...requestedProducts);
        }

        this.totalCount = requestedProductPage.totalItems!;

      } catch {
        this.showMessage("There was an issue loading requested products. Please refresh the page to try again.");
      } finally {
        this.loading = false;
        this.runPendingLoadProducts();
      }
    }

  }

  private runPendingLoadProducts() {
    if (!this.loadProductsPending) return;
    this.loadProductsPending = false;
    const clear = this.loadProductsPendingClearExisting;
    Promise.resolve().then(() => this.loadProducts(clear));
  }

  async loadFilterOptions(request: GetListProductsFiltersRequest | GetListRequestedProductsFiltersRequest): Promise<GetListProductsFiltersResponse | GetListRequestedProductsFiltersResponse> {
    if (request instanceof GetListProductsFiltersRequest) {
      return this.loadProductsFilterOptions(request);
    } else {
      return this.loadRequestedProductsFilterOptions(request);
    }
  }
  
  async loadProductsFilterOptions(request: GetListProductsFiltersRequest): Promise<GetListProductsFiltersResponse> {
    this.loadingFilterOption = true;

    try {
      var response = await this.situViewClient.getListProductsFilters(request);

      return response;
    } catch (error) {
      console.log(error);
    } finally {
      this.loadingFilterOption = false;
    }

    return new GetListProductsFiltersResponse();
  }
  
  async loadRequestedProductsFilterOptions(request: GetListRequestedProductsFiltersRequest): Promise<GetListRequestedProductsFiltersResponse> {
    this.loadingFilterOption = true;

    try {
      var response = await this.situViewClient.getListRequestedProductsFilters(request);

      return response;
    } catch (error) {
      console.log(error);
    } finally {
      this.loadingFilterOption = false;
    }

    return new GetListRequestedProductsFiltersResponse();
  }

  buildListRequest() : NewListProductsRequest | ListRequestedProductsRequest {
    if (!this.requestedProductsFilter) {
      return this.buildListProductsRequest();
    } else {
      return this.buildListRequestedProductsRequest();
    }
  }
  
  buildFilterOptionsRequest() : GetListProductsFiltersRequest | GetListRequestedProductsFiltersRequest {
    if (!this.requestedProductsFilter) {
      return this.buildProductsFilterOptionsRequest();
    } else {
      return this.buildRequestedProductsFilterOptionsRequest();
    }
  }

  buildListProductsRequest() : NewListProductsRequest {
    var includeProductSpecifications = !this.isCatalog;

    var request = new NewListProductsRequest();
    request.companyId = this.selectedCompany?.id!;

    request.filterCompanyId = this.companyIdFilter;
    request.filterBrandId = this.brandIdFilter;
    request.filterProductClassificationId = this.productClassificationIdFilter;
    request.filterProductTypeId = this.productTypeIdFilter;
    request.filterProductTypeTemplateName = this.templateNameFilter;
    request.filterProductState = this.productStateFilter;
    request.filterRangeId = this.productRangeIdFilter;
    request.filterTagGroups = this.tagGroupsFilter;
    request.filterProductSpecificationGroups = this.productSpecificationFilterGroups;
    request.filterEnabled = this.enabledProductsFilter;
    request.searchTerm = this.searchTerm;

    this.hasFilters = (
      this.companyIdFilter !== undefined ||
      this.brandIdFilter !== undefined ||
      this.productClassificationIdFilter !== undefined ||
      this.productTypeIdFilter !== undefined ||
      this.productStateFilter !== undefined ||
      this.productRangeIdFilter !== undefined ||
      this.tagGroupsFilter !== undefined ||
      this.productSpecificationFilterGroups !== undefined ||
      this.enabledProductsFilter === false
    );

    request.orderRelatedTags = this.relatedTagsOrder;

    if (this.orderByDateModified) {
      request.orderBy = ListProductsRequestOrderField.DateModified;
      request.orderDescending = true;
    }

    request.pageNumber = this.pageNumber;
    request.pageSize = this.pageSize;

    request.includeProductSpecifications = includeProductSpecifications;
    request.includeRanges = this.includeRanges;
    request.includeThemeUsage = true;

    return request;
  }
      
  buildProductsFilterOptionsRequest() : GetListProductsFiltersRequest {
    var request = this.buildListProductsRequest();
    return GetListProductsFiltersRequest.fromJS(request);
  }

  buildListRequestedProductsRequest() : ListRequestedProductsRequest {
    var request = new ListRequestedProductsRequest();
    request.companyId = this.selectedCompany?.id!;

    request.filterSupplierId = this.companyIdFilter;
    request.filterRequestedProductState = this.productStateFilter;
    request.filterProductClassificationId = this.productClassificationIdFilter;
    request.filterProductTypeId = this.productTypeIdFilter;
    request.filterProductTypeTemplateName = this.templateNameFilter;
    request.filterBrandId = this.brandIdFilter;
    request.searchTerm = this.searchTerm;

    this.hasFilters = (
      this.companyIdFilter !== undefined ||
      this.productStateFilter !== undefined ||
      this.productClassificationIdFilter !== undefined ||
      this.productTypeIdFilter !== undefined ||
      this.brandIdFilter !== undefined ||
      this.enabledProductsFilter === false
    );

    request.pageNumber = this.pageNumber;
    request.pageSize = this.pageSize;

    return request;
  }
      
  buildRequestedProductsFilterOptionsRequest() : GetListRequestedProductsFiltersRequest {
    var request = this.buildListProductsRequest();
    return GetListRequestedProductsFiltersRequest.fromJS(request);
  }

  clearAllFilterOptions(optionsToKeep?: any[]) {
    if (optionsToKeep != this.companyFilterOptions) {
      this.companyFilterOptions = undefined;
    } 
    if (optionsToKeep != this.brandFilterOptions) {
      this.brandFilterOptions = undefined;
    }
    if (optionsToKeep != this.productRangeFilterOptions) {
      this.productRangeFilterOptions = undefined;
    }
    if (optionsToKeep != this.productClassificationFilterOptions) {
      this.productClassificationFilterOptions = undefined;
    }
    if (optionsToKeep != this.productTypeFilterOptions) {
      this.productTypeFilterOptions = undefined;
    }
    if (optionsToKeep != this.productStateFilterOptions) {
      this.productStateFilterOptions = undefined;
    }
  }

  async loadAllFilterOptions() {
      var productsRequest = new GetListProductsFiltersRequest();
      productsRequest.companyId = this.selectedCompany?.id;

      productsRequest.includeFilterCompanyOptions = true;
      productsRequest.includeFilterBrandOptions = true;
      productsRequest.includeFilterRangeOptions = true;
      productsRequest.includeFilterProductClassificationOptions = true;
      productsRequest.includeFilterProductTypeOptions = true;
      productsRequest.includeFilterProductStateOptions = true;

      var productsResponse = await this.loadProductsFilterOptions(productsRequest);

      this.companyFilterOptions = productsResponse.filterCompanyOptions;
      this.brandFilterOptions = productsResponse.filterBrandOptions;
      this.productRangeFilterOptions = productsResponse.filterRangeOptions;
      this.productClassificationFilterOptions = productsResponse.filterProductClassificationOptions;
      this.productTypeFilterOptions = productsResponse.filterProductTypeOptions;
      this.productStateFilterOptions = productsResponse.filterProductStateOptions;

      this.loadFilterCompanyOptions();
      this.loadFilterBrandOptions();
      this.loadFilterProductRangeOptions();
      this.loadFilterProductClassificationOptions();
      this.loadFilterProductTypeOptions();
      this.loadFilterProductStateOptions();
  }
  
  //#region Company AutoComplete Filter

  companyControl = new FormControl();
  filteredCompanies: Observable<CompanyFilterOption[]> | undefined;
  companyFilterOptions?: CompanyFilterOption[];
  allCompanies: Company[] = [];

  async loadFilterCompanyOptions() {
    if (!this.loadingFilterOption && !this.companyFilterOptions) {
      if (!this.requestedProductsFilter) {
        var productsRequest = this.buildProductsFilterOptionsRequest();
        productsRequest.includeFilterCompanyOptions = true;
        var productsResponse = await this.loadProductsFilterOptions(productsRequest);
        this.companyFilterOptions = productsResponse.filterCompanyOptions;
      } else {
        var requestedProductsRequest = this.buildRequestedProductsFilterOptionsRequest();
        requestedProductsRequest.includeFilterSupplierOptions = true;
        var requestedProductsResponse = await this.loadRequestedProductsFilterOptions(requestedProductsRequest);
        this.companyFilterOptions = requestedProductsResponse.filterSupplierOptions;
      }
    }

    this.companyNameFilter = this.companyFilterOptions?.find(x => x.id === this.companyIdFilter)?.name;
    this.companyControl.setValue(this.companyNameFilter);
    this.companyControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  private async loadCompanies() {
    this.allCompanies = await this.companyViewService.LoadCompanies();
  }

  onCompanySelected(event: any): void {
    this.selectCompanyByName(event.option.value);

    this.onFilterCompanyId.emit(this.companyIdFilter);

    this.clearAllFilterOptions(this.companyFilterOptions);
  }

  selectCompanyByName(companyName: string) {
    this.companyIdFilter = this.companyFilterOptions?.find(x => x.name === companyName)?.id;
    this.companyNameFilter = companyName;
      
    this.filterProducts();
  }

  selectCompanyById(companyId: string) {
    this.companyIdFilter = companyId;
    this.companyNameFilter = this.companyFilterOptions?.find(x => x.id === companyId)?.name;
      
    this.filterProducts();
  }

  selectCompanyByProductCompanyId(productCompanyId: string, filterProducts = true) {
    this.companyIdFilter = productCompanyId;
    this.companyNameFilter = this.allCompanies?.find(x => x.id === productCompanyId)?.name;

    this.companyControl.setValue(this.companyNameFilter);
    this.companyControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    this.onFilterCompanyId.emit(this.companyIdFilter);
      
    if (filterProducts) {
      this.filterProducts();
    }
  }

  onClearCompany(filterProducts = true) {
    this.onFilterCompanyId.emit(undefined);
    this.clearCompany(filterProducts);
  }

  clearCompany(filterProducts = true) {
    if (this.companyNameFilter) {
      this.clearAllFilterOptions();
    }

    this.companyControl.reset();
    this.companyIdFilter = undefined;
    this.companyNameFilter = undefined;
    
    if (filterProducts) {
      this.filterProducts();
    }
  }

  private filterCompanies(searchTerm: string | undefined = undefined): CompanyFilterOption[] {
    var companies = this.companyFilterOptions;

    if (!companies) {
      return [];
    }

    // Filter Company Names by the supplied Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      companies = companies.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return companies;
  }

  //#endregion Company AutoComplete Filter

  //#region Brand AutoComplete Filter

  brandControl = new FormControl();
  filteredBrands: Observable<BrandFilterOption[]> | undefined;
  brandFilterOptions?: BrandFilterOption[];

  async loadFilterBrandOptions() {
    if (!this.loadingFilterOption && !this.brandFilterOptions) {
      var request = this.buildFilterOptionsRequest();
      request.includeFilterBrandOptions = true;
      var response = await this.loadFilterOptions(request);
      
      this.brandFilterOptions = response.filterBrandOptions;
    }

    this.brandNameFilter = this.brandFilterOptions?.find(x => x.id === this.brandIdFilter)?.name;
    this.brandControl.setValue(this.brandIdFilter);
    this.brandControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onBrandSelected(event: any): void {
    this.selectBrandById(event.option.value);

    this.onFilterBrandId.emit(this.brandIdFilter);

    this.clearAllFilterOptions(this.brandFilterOptions);
  }

  displayBrandName = (brandId: string): string => {
    if (!brandId) return '';
    return this.brandFilterOptions?.find(x => x.id === brandId)?.name ?? brandId;
  }

  selectBrandByName(brandName: string) {
    this.brandIdFilter = this.brandFilterOptions?.find(x => x.name === brandName)?.id;
    this.brandNameFilter = brandName;
      
    this.filterProducts();
  }

  selectBrandById(brandId: string) {
    this.brandIdFilter = brandId;
    this.brandNameFilter = this.brandFilterOptions?.find(x => x.id === brandId)?.name;
      
    this.filterProducts();
  }

  selectBrandByProductBrandId(productBrandId: string, filterProducts = true) {
    this.brandIdFilter = productBrandId;
    this.brandNameFilter = this.brandFilterOptions?.find(x => x.id === productBrandId)?.name;

    this.brandControl.setValue(this.brandIdFilter);
    this.brandControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    this.onFilterBrandId.emit(this.brandIdFilter);
      
    if (filterProducts) {
      this.filterProducts();
    }
  }

  onClearBrand(filterProducts = true) {
    this.clearBrand(filterProducts);
    this.onFilterBrandId.emit(undefined);
  }

  clearBrand(filterProducts = true) {
    if (this.brandNameFilter) {
      this.clearAllFilterOptions();
    }

    this.brandControl.reset();
    this.brandIdFilter = undefined;
    this.brandNameFilter = undefined;
    
    if (filterProducts) {
      this.filterProducts();
    }
  }

  private filterBrands(searchTerm: string | undefined = undefined): BrandFilterOption[] {
    var brands = this.brandFilterOptions;

    if (!brands) {
      return [];
    }

    // Filter Brand Names by the supplied Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      brands = brands.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return brands;
  }

  //#endregion Brand AutoComplete Filter

  //#region Product Range AutoComplete Filter

  productRangeControl = new FormControl();
  filteredProductRanges: Observable<RangeFilterOption[]> | undefined;
  productRangeFilterOptions?: RangeFilterOption[];
  
  async loadFilterProductRangeOptions() {
    if (!this.loadingFilterOption && !this.productRangeFilterOptions) {
      if (!this.requestedProductsFilter) {
        var request = this.buildProductsFilterOptionsRequest();
        request.includeFilterRangeOptions = true;
        var response = await this.loadProductsFilterOptions(request);

        this.productRangeFilterOptions = response.filterRangeOptions;
      } else {
        this.productRangeFilterOptions = []; // Requested products can not be part of a range
      }
    }

    var currentRangeFilter = this.productRangeFilterOptions?.find(x => x.id === this.productRangeIdFilter);
    this.productRangeNameFilter = currentRangeFilter?.name;
    this.productRangeControl.setValue(currentRangeFilter);
    this.productRangeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onProductRangeSelected(event: any): void {
    this.selectProductRange(event.option.value);

    this.onFilterProductRangeId.emit(this.productRangeIdFilter);

    this.clearAllFilterOptions(this.productRangeFilterOptions);
  }

  selectProductRange(range: RangeFilterOption) {
    this.productRangeIdFilter = range?.id;
    this.productRangeNameFilter = range?.name;
    
    this.filterProducts();
  }

  displayRange(range: RangeFilterOption): string {
    return range?.name ?? '';
  }

  selectProductRangeById(productRangeId: string) {
    this.productRangeIdFilter = productRangeId;
    var range = this.productRangeFilterOptions?.find(x => x.id === productRangeId);
    this.productRangeNameFilter = range?.name;
    
    this.filterProducts();
  }

  onClearProductRange(filterProducts = true) {
    this.clearProductRange(filterProducts);

    this.onFilterProductRangeId.emit(undefined);
  }

  clearProductRange(filterProducts = true) {
    if (this.productRangeNameFilter) {
      this.clearAllFilterOptions();
    }

    this.productRangeControl.reset();
    this.productRangeIdFilter = undefined;
    this.productRangeNameFilter = undefined;
    
    if (filterProducts) {
      this.filterProducts();
    }
  }

  private filterProductRanges(searchTerm: string | undefined = undefined): RangeFilterOption[] {
    var productRanges = this.productRangeFilterOptions;

    if (!productRanges) {
      return [];
    }

    if (this.templateNameFilter) {
      let name = this.templateNameFilter.toLowerCase()
      productRanges = productRanges
        .filter(e => e.name?.toLowerCase() === name)
    }

    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productRanges = productRanges.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    if (this.companyIdFilter) {
      productRanges = productRanges = productRanges.sort((a, b) => {
        var aMatches = a.companyId === this.companyIdFilter;
        var bMatches = b.companyId === this.companyIdFilter;

        if (aMatches && !bMatches) return -1;
        if (!aMatches && bMatches) return 1;
        return 0; // maintain original order otherwise
      });
    }

    return productRanges;
  }

  //#endregion Product Range AutoComplete Filter

  //#region Product Classification AutoComplete Filter

  productClassificationControl = new FormControl();
  filteredProductClassifications: Observable<ProductClassificationFilterOption[]> | undefined;
  productClassificationFilterOptions?: ProductClassificationFilterOption[];
  
  async loadFilterProductClassificationOptions() {
    if (!this.loadingFilterOption && !this.productClassificationFilterOptions) {
      var request = this.buildFilterOptionsRequest();
      request.includeFilterProductClassificationOptions = true;
      var response = await this.loadFilterOptions(request);

      this.productClassificationFilterOptions = response.filterProductClassificationOptions;
    }

    this.productClassificationNameFilter = this.productClassificationFilterOptions?.find(x => x.id === this.productClassificationIdFilter)?.name;
    this.productClassificationControl.setValue(this.productClassificationNameFilter);
    this.productClassificationControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onProductClassificationSelected(event: any): void {
    this.selectProductClassificationByName(event.option.value);

    this.onFilterProductClassificationId.emit(this.productClassificationIdFilter);

    this.clearAllFilterOptions(this.productClassificationFilterOptions);
  }

  selectProductClassificationByName(productClassificationName: string) {
    this.productClassificationIdFilter = this.productClassificationFilterOptions?.find(x => x.name === productClassificationName)?.id;
    this.productClassificationNameFilter = productClassificationName;
      
    this.filterProducts();
  }

  selectProductClassificationById(productClassificationId: string) {
    this.productClassificationIdFilter = productClassificationId;
    this.productClassificationNameFilter = this.productClassificationFilterOptions?.find(x => x.id === productClassificationId)?.name;
      
    this.filterProducts();
  }

  selectProductClassificationByProduct(productClassificationId: string) {
    this.productClassificationIdFilter = productClassificationId;
    this.productClassificationNameFilter = this.productClassificationFilterOptions?.find(x => x.id === productClassificationId)?.name;

    this.productClassificationControl.setValue(this.productClassificationNameFilter);
    this.productClassificationControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    this.onFilterProductClassificationId.emit(this.productClassificationIdFilter);
      
    this.filterProducts();
  }

  onClearProductClassification(filterProducts = true) {
    this.clearProductClassification(filterProducts);

    this.onFilterProductClassificationId.emit(undefined);
  }

  clearProductClassification(filterProducts = true) {
    if (this.productClassificationNameFilter) {
      this.clearAllFilterOptions();
    }

    this.productClassificationControl.reset();
    this.productClassificationIdFilter = undefined;
    this.productClassificationNameFilter = undefined;
    
    if (filterProducts) {
      this.filterProducts();
    }
  }

  private filterProductClassifications(searchTerm: string | undefined = undefined): ProductClassificationFilterOption[] {
    var productClassifications = this.productClassificationFilterOptions;

    if (!productClassifications) {
      return [];
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productClassifications = productClassifications.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return productClassifications;
  }

  //#endregion Product Classification AutoComplete Filter

  //#region Product Type AutoComplete Filter

  productTypeControl = new FormControl();
  filteredProductTypes: Observable<ProductTypeWithColor[]> | undefined;
  productTypeFilterOptions?: ProductTypeFilterOption[];
  allProductTypes: ProductTypeWithColor[] = [];
  
  async loadFilterProductTypeOptions() {
    if (!this.loadingFilterOption && !this.productTypeFilterOptions) {
      var request = this.buildFilterOptionsRequest();
      request.includeFilterProductTypeOptions = true;
      var response = await this.loadFilterOptions(request);

      this.productTypeFilterOptions = response.filterProductTypeOptions;
    }

    this.productTypeNameFilter = this.productTypeFilterOptions?.find(x => x.id === this.productTypeIdFilter)?.name;
    this.productTypeControl.setValue(this.productTypeNameFilter);
    this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }
  
  private async loadProductTypes() {
    this.allProductTypes = await this.productService.LoadProductTypesWithColor();

    this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === this.productTypeIdFilter)?.name;
    this.productTypeControl.setValue(this.productTypeNameFilter);
    this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onProductTypeSelected(event: any): void {
    this.selectProductTypeByName(event.option.value);

    this.onFilterProductTypeId.emit(this.productTypeIdFilter);

    this.clearAllFilterOptions(this.productTypeFilterOptions);
  }

  selectProductTypeByName(productTypeName: string) {
    this.productTypeIdFilter = this.allProductTypes?.find(x => x.name === productTypeName)?.id;
    this.productTypeNameFilter = productTypeName;

    this.clearProductSpecifications(false);
    this.filterProducts();
  }

  selectProductTypeById(productTypeId: string) {
    this.productTypeIdFilter = productTypeId;
    this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === productTypeId)?.name;

    this.clearProductSpecifications(false);
    this.filterProducts();
  }

  selectProductTypeByProduct(productTypeId: string) {
    this.productTypeIdFilter = productTypeId;
    this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === productTypeId)?.name;

    this.productTypeControl.setValue(this.productTypeNameFilter);
    this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    this.onFilterProductTypeId.emit(this.productTypeIdFilter);

    this.clearProductSpecifications(false);
    this.filterProducts();
  }

  onClearProductType(filterProducts = true) {
    this.clearProductType(filterProducts);

    this.onFilterProductTypeId.emit(undefined);
  }

  clearProductType(filterProducts = true) {
    if (this.productTypeNameFilter) {
      this.clearAllFilterOptions();
    }

    this.productTypeControl.reset();
    this.productTypeIdFilter = undefined;
    this.productTypeNameFilter = undefined;

    this.clearProductSpecifications(false);

    if (filterProducts) {
      this.filterProducts();
    }
  }

  //#region Product Specification Filter

  onProductSpecificationFilterGroupsChange(groups: ProductSpecificationFilterGroup[]) {
    this.productSpecificationFilterGroups = groups && groups.length ? groups : undefined;
    this.onFilterProductSpecificationGroups.emit(this.productSpecificationFilterGroups ?? []);
    this.filterProducts();
  }

  onProductSpecificationSelectedIdsChange(ids: string[]) {
    this.productSpecificationSelectedIds = ids ?? [];
    this.onFilterProductSpecificationSelectedIds.emit(this.productSpecificationSelectedIds);
  }

  clearProductSpecifications(filterProducts = true) {
    const hadSelection = (this.productSpecificationSelectedIds?.length ?? 0) > 0
      || (this.productSpecificationFilterGroups?.length ?? 0) > 0;

    this.productSpecificationFilterGroups = undefined;
    this.productSpecificationSelectedIds = [];

    if (hadSelection) {
      this.onFilterProductSpecificationGroups.emit([]);
      this.onFilterProductSpecificationSelectedIds.emit([]);

      if (filterProducts) {
        this.filterProducts();
      }
    }
  }

  //#endregion Product Specification Filter

  private filterProductTypes(searchTerm: string | undefined = undefined): ProductTypeWithColor[] {
    var allProductTypes = this.allProductTypes;
    var productTypeFilterOptions = this.productTypeFilterOptions;

    if (!productTypeFilterOptions) {
      return [];
    }

    // Filter by availiable options
    allProductTypes = allProductTypes
      .filter(e => productTypeFilterOptions!.some(x => x.id === e.id));

    // Filter by Template Name
    if (this.templateNameFilter) {
      var templateName = this.templateNameFilter.toLowerCase()
      allProductTypes = allProductTypes
        .filter(e => e.templateName?.toLowerCase() === templateName);
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      allProductTypes = allProductTypes.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return allProductTypes;
  }

  //#endregion Product Type AutoComplete Filter

  //#region Product State AutoComplete Filter

  productStateControl = new FormControl();
  filteredProductStates: Observable<ProductStateFilterOption[]> | undefined;
  productStateFilterOptions?: ProductStateFilterOption[];

  async loadFilterProductStateOptions() {
    if (!this.loadingFilterOption && !this.productStateFilterOptions) {
      var request = this.buildFilterOptionsRequest();
      request.includeFilterProductStateOptions = true;
      var response = await this.loadFilterOptions(request);

      this.productStateFilterOptions = response.filterProductStateOptions;
    }

    this.productStateNameFilter = this.productStateFilterOptions?.find(x => x.state === this.productStateFilter)?.name;
    this.productStateControl.setValue(this.productStateNameFilter);
    this.productStateControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onProductStateSelected(event: any): void {
    this.selectProductStateByName(event.option.value);

    this.onFilterProductState.emit(this.productStateFilter);

    this.clearAllFilterOptions(this.productStateFilterOptions);
  }

  selectProductState(productState: ProductWorkflowState) {
    this.productStateFilter = productState;
    this.productStateNameFilter = this.productStateFilterOptions?.find(x => x.state === productState)?.name;
      
    this.filterProducts();
  }

  selectProductStateByName(productStateName: string) {
    this.productStateFilter = this.productStateFilterOptions?.find(x => x.name === productStateName)?.state;
    this.productStateNameFilter = productStateName;
      
    this.filterProducts();
  }

  onClearProductState(filterProducts = true) {
    this.clearProductState(filterProducts);

    this.onFilterProductState.emit(undefined);
  }

  clearProductState(filterProducts = true) {
    if (this.productStateNameFilter) {
      this.clearAllFilterOptions();
    }

    this.productStateControl.reset();
    this.productStateFilter = undefined;
    this.productStateNameFilter = undefined;
    
    if (filterProducts) {
      this.filterProducts();
    }
  }

  private filterProductStates(searchTerm: string | undefined = undefined): ProductWorkflow[] {
    var productStates = this.productStateFilterOptions;

    if (!productStates) {
      return [];
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productStates = productStates.filter(x => x.name).filter(state => state.name!.toLowerCase().includes(searchTerm!));
    }

    return productStates;
  }

  //#endregion Product State AutoComplete Filter

  onClearAllFilters() {
    var hasFilters = this.hasFilters;

    this.onClearCompany(false);
    this.onClearBrand(false);
    this.onClearProductClassification(false);
    this.onClearProductType(false);
    this.onClearProductState(false);
    this.onClearProductRange(false);
    this.clearProductSpecifications(false);

    // Clear only on second click
    if (!hasFilters) {
      this.clearSearchTerm();
    }

    this.enabledProductsFilter = true;
    
    this.clearAllFilterOptions();

    this.filterProducts();
  }

  isDefaultId(id: string){
    return id === Guid.EMPTY;
  }

  pluralize(unit: string, value: number): string {
    return Utils.pluralize(unit, value);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'Dismiss', {
      duration: 3000
    });
  }
}
